/** @jsx jsx */
import { jsx, Flex, Box } from 'theme-ui';
import { Themed } from '@theme-ui/mdx';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import Link from './Link';
import Spinner from './Spinner';
import { TicketIcon, TicketSeriesIcon } from './Icon';
import { loadRoutePrices } from '../state/routePrices';
import { formatPrice } from '../utils/formatters';
import { contentfulText, localizeTicketLink } from '../utils/ticketRoutes';
import useRouteCardTracking from '../hooks/useRouteCardTracking';

// link looks like https://liput.matkahuolto.fi/connectionlist?departurePlaceId=p1001&arrivalPlaceId=p1317
// The route id used to look up the price is `${departurePlaceId}-${arrivalPlaceId}`, e.g. p1001-p1317.
const routeIdFromLink = (link) => {
  if (!link) return null;
  try {
    const { searchParams } = new URL(link);
    const dep = searchParams.get('departurePlaceId');
    const arr = searchParams.get('arrivalPlaceId');
    return dep && arr ? `${dep}-${arr}` : null;
  } catch {
    return null;
  }
};

export default ({ title, routes = [], frontPage = false }) => {
  const dispatch = useDispatch();
  const prices = useSelector((state) => state.routePrices.prices);
  const { i18n } = useTranslation();

  const cards = useMemo(() => routes.filter((card) => !!card), [routes]);
  const needsRoutePrices = useMemo(
    () => cards.some((card) => !contentfulText(card.routeName) || !contentfulText(card.ctaButtonText)),
    [cards]
  );

  useEffect(() => {
    if (needsRoutePrices) {
      dispatch(loadRoutePrices());
    }
  }, [dispatch, needsRoutePrices]);

  if (!cards.length) {
    return null;
  }

  if (needsRoutePrices && prices === null) {
    return (
      <div sx={{ position: 'relative', height: '100px', width: '100%' }}>
        <Spinner />
      </div>
    );
  }

  const displayCards = cards
    .map((card) => {
      const routeNameOverride = contentfulText(card.routeName);
      const ctaButtonTextOverride = contentfulText(card.ctaButtonText);
      const route = prices?.[routeIdFromLink(card.link)];
      const formattedPrice = typeof route?.price === 'number' ? formatPrice(route.price) : null;
      const automaticCtaButton = formattedPrice
        ? card.ctaPriceButtonPrefix
          ? `${card.ctaPriceButtonPrefix} ${formattedPrice}`
          : formattedPrice
        : null;

      return {
        card,
        name: routeNameOverride || contentfulText(route?.name),
        ctaButtonText: ctaButtonTextOverride || automaticCtaButton,
        link: localizeTicketLink(card.link, i18n.language),
      };
    })
    .filter(({ name, ctaButtonText, link }) => name && ctaButtonText && link);

  if (!displayCards.length) {
    return null;
  }

  return (
    <Flex sx={{ flexDirection: 'column', width: '100%', mb: frontPage ? 0 : 4 }}>
      {title &&
        (frontPage ? (
          <Themed.h2 sx={{ variant: 'text.title', mt: 0, mb: 4, textAlign: 'center' }}>{title}</Themed.h2>
        ) : (
          <h2 sx={{ m: 0, mb: 3, textAlign: 'center' }}>{title}</h2>
        ))}
      <Flex
        sx={{
          flexDirection: ['column', null, null, 'row'],
          justifyContent: [null, null, null, 'center'],
          overflowX: 'auto',
          maxWidth: '100%',
          width: ['100%', null],
          scrollSnapType: 'x mandatory',
          gap: 2,
          MsOverflowStyle: frontPage ? 'none' : '-ms-autohiding-scrollbar',
          ...(frontPage && {
            scrollbarWidth: 'none',
            '::-webkit-scrollbar': { display: 'none' },
          }),
        }}
      >
        {displayCards.map(({ card, name, ctaButtonText, link }, i) => (
          <TicketRouteCardSmall
            {...card}
            name={name}
            ctaButtonText={ctaButtonText}
            link={link}
            position={i + 1}
            key={card?.contentful_id || i}
          />
        ))}
      </Flex>
    </Flex>
  );
};

// Mirrors the ParcelWidget layout from AllParcelWidgets.js (column card with a top ribbon slot
// and a row→column main-content using space-between + flex-weighted slots). The one intentional
// difference is the wrapper: this card is a Link (external ticket URL) rather than an onClick button.
const TicketRouteCardSmall = ({ ribbon, ribbonColor, icon, content, ctaButtonText, link, name, position }) => {
  const { ref, onClick } = useRouteCardTracking(name, position, link);

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flexShrink: 0,
        scrollSnapAlign: 'start',
        maxWidth: [null, null, null, 164],
        position: 'relative',
      }}
    >
      <Link
        to={link}
        noTitleFix
        onClick={onClick}
        sx={{
          textDecoration: 'none',
          color: 'text',
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'border',
          overflow: 'hidden',
          flexDirection: 'column',
          display: 'flex',
          width: '100%',
          height: '100%',
          ':hover': {
            borderColor: 'grayLight',
          },
        }}
      >
        <Flex
          sx={{
            display: ['none', null, null, 'flex'],
            bg: ribbon ? ribbonColor || 'primary' : null,
            flexDirection: 'column',
            color: 'white',
            fontSize: '11px',
            width: '100%',
            minHeight: '26px',
            py: 1,
            px: 2,
            justifyContent: 'center',
          }}
        >
          <span sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>{ribbon}</span>
        </Flex>

        <Flex
          sx={{
            height: '100%',
            px: 3,
            pt: ribbon ? ['40px', null, null, 2] : [4, null, null, 2],
            pb: [4, null, null, 3],
            flexDirection: ['row', null, null, 'column'],
            alignItems: ['center', null, null, 'flex-start'],
            justifyContent: 'space-between',
            gap: [3, null, null, 0],
            minWidth: 0,
          }}
        >
          <Flex
            sx={{
              flex: [1, null, null, 'none'],
              minWidth: 0,
              width: '100%',
              alignItems: ['center', null, null, 'flex-start'],
              flexDirection: ['row', null, null, 'column'],
              gap: [3, null, null, 0],
            }}
          >
            <Flex
              sx={{
                flex: ['none', null, null, 'none'],
                alignItems: 'center',
                alignSelf: ['stretch', null, null, 'auto'],
                justifyContent: ['flex-start', null, null, 'flex-start'],
                ml: [2, null, null, 0],
                py: [0, null, null, 3],
                minWidth: 40,
              }}
            >
              {ribbon && (
                <div
                  sx={{
                    display: ['inline-block', null, null, 'none'],
                    bg: ribbonColor || 'primary',
                    color: 'white',
                    borderRadius: 1,
                    py: 1,
                    px: 2,
                    fontSize: '11px',
                    position: 'absolute',
                    top: '5px',
                    left: '5px',
                  }}
                >
                  {ribbon}
                </div>
              )}
              {icon === 'round-trip' ? (
                <TicketSeriesIcon width="40" height="40" aria-hidden="true" />
              ) : (
                <TicketIcon width="40" height="40" aria-hidden="true" />
              )}
            </Flex>

            <Flex
              sx={{
                flex: [1, null, null, 2],
                minWidth: 0,
                width: [null, null, null, 130],
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: [1, null, null, 2],
              }}
            >
              {name && (
                <span
                  sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    pr: [0, null, null, 2],
                    fontSize: 18,
                    fontWeight: 'medium',
                    textAlign: 'left',
                    hyphens: 'auto',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {name}
                </span>
              )}
              {content && (
                <span
                  sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    pr: [0, null, null, 2],
                    color: 'black',
                    fontSize: 14,
                    textAlign: 'left',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {content}
                </span>
              )}
            </Flex>
          </Flex>

          <Flex
            sx={{
              display: 'flex',
              flex: ['0 0 auto', null, null, 0],
              flexShrink: 0,
              mt: [0, null, null, 3],
              justifyContent: ['flex-end', null, null, 'flex-start'],
              mx: 0,
              minWidth: 0,
            }}
          >
            <Button as="span" variant="small" sx={{ flexShrink: 0, whiteSpace: 'nowrap', textAlign: 'center' }}>
              {ctaButtonText}
            </Button>
          </Flex>
        </Flex>
      </Link>
    </Box>
  );
};
